import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

const PLUTO_API_GATEWAY_PREFIX = "/sub-system/pluto";

function stripTrailingSlash(url: string): string {
    return url.endsWith("/") === true ? url.slice(0, -1) : url;
}

/**
 * Public media base from `MEDIA_LIBRARY_PUBLIC_BASE_URL`.
 * Accepts either the CDN origin (`https://staging.cdn.intellsys.ai`) or the gateway base
 * (`https://staging.cdn.intellsys.ai/sub-system/pluto`).
 */
function resolveMediaLibraryPublicGatewayBase(publicBaseUrl: string): string {
    const trimmed = stripTrailingSlash(publicBaseUrl.trim());
    if (trimmed === "") {
        return trimmed;
    }
    if (trimmed.endsWith(PLUTO_API_GATEWAY_PREFIX) === true) {
        return trimmed;
    }
    return `${trimmed}${PLUTO_API_GATEWAY_PREFIX}`;
}

/**
 * CDN / public media origin from Neptune env. No hostname rewriting.
 */
export function toMediaLibraryCdnBaseUrl(_intellsysBaseUrl?: string): string {
    const gatewayBase = resolveMediaLibraryPublicGatewayBase(
        String(import.meta.env.MEDIA_LIBRARY_PUBLIC_BASE_URL ?? ""),
    );
    if (gatewayBase.endsWith(PLUTO_API_GATEWAY_PREFIX) === true) {
        return gatewayBase.slice(0, -PLUTO_API_GATEWAY_PREFIX.length);
    }
    return gatewayBase;
}

/** Public media URLs use MEDIA_LIBRARY_PUBLIC_BASE_URL; admin APIs keep using intellsysBaseUrl. */
function plutoPublicMediaBase(_intellsysBaseUrl?: string): string {
    return resolveMediaLibraryPublicGatewayBase(String(import.meta.env.MEDIA_LIBRARY_PUBLIC_BASE_URL ?? ""));
}

/**
 * Encode a media-library path for use in URL path segments.
 * Accepts `/folder/file.jpg` or `folder/file.jpg`; omits a leading slash in the result.
 */
export function encodeMediaLibraryPathForUrl(path: string): string {
    const trimmed = path.trim();
    const withoutLeadingSlash = trimmed.startsWith("/") === true ? trimmed.slice(1) : trimmed;
    if (withoutLeadingSlash === "") {
        return "";
    }
    return withoutLeadingSlash
        .split("/")
        .map((segment) => encodeURIComponent(segment))
        .join("/");
}

export function normalizeMediaLibraryPath(path: string): string {
    const trimmed = path.trim();
    if (trimmed === "" || trimmed === "/") {
        return "/";
    }
    const withoutTrailingSlash = trimmed.endsWith("/") === true ? trimmed.slice(0, -1) : trimmed;
    if (withoutTrailingSlash === "") {
        return "/";
    }
    return withoutTrailingSlash.startsWith("/") === true ? withoutTrailingSlash : `/${withoutTrailingSlash}`;
}

/** Parent of a library path. `/a/b/file.jpg` → `/a/b`. Root stays `/`. */
export function parentMediaLibraryPath(path: string): string {
    const normalized = normalizeMediaLibraryPath(path);
    if (normalized === "/") {
        return "/";
    }
    const segments = normalized.split("/").filter((segment) => segment !== "");
    segments.pop();
    if (segments.length === 0) {
        return "/";
    }
    return `/${segments.join("/")}`;
}

/** Last segment of a library path, or null at root. */
export function mediaLibraryPathName(path: string): string | null {
    const normalized = normalizeMediaLibraryPath(path);
    if (normalized === "/") {
        return null;
    }
    const segments = normalized.split("/").filter((segment) => segment !== "");
    return segments[segments.length - 1] ?? null;
}

export function joinMediaLibraryPath(parentPath: string, name: string): string {
    const trimmedName = name.trim();
    if (trimmedName === "") {
        return normalizeMediaLibraryPath(parentPath);
    }
    const parent = normalizeMediaLibraryPath(parentPath);
    if (parent === "/") {
        return `/${trimmedName}`;
    }
    return `${parent}/${trimmedName}`;
}

export function replaceMediaLibraryPathName(path: string, name: string): string {
    return joinMediaLibraryPath(parentMediaLibraryPath(path), name);
}

/**
 * Library path encoded after the config page id.
 * `/w/{workspaceId}/{pageKey}` is the library root; extra segments are the folder or asset.
 */
export function readMediaLibraryPathFromBrowserUrl(pathname: string, workspaceId: string): string {
    const pathOnly = pathname.split("?")[0]?.split("#")[0] ?? pathname;
    const prefix = `/w/${workspaceId}/`;
    if (pathOnly.startsWith(prefix) !== true) {
        return "/";
    }
    const afterWorkspace = pathOnly.slice(prefix.length);
    const pageKeySeparator = afterWorkspace.indexOf("/");
    if (pageKeySeparator === -1) {
        return "/";
    }
    const encodedLibraryPath = afterWorkspace.slice(pageKeySeparator + 1);
    if (encodedLibraryPath === "") {
        return "/";
    }
    const decoded = encodedLibraryPath
        .split("/")
        .filter((segment) => segment !== "")
        .map((segment) => {
            try {
                return decodeURIComponent(segment);
            } catch {
                return segment;
            }
        })
        .join("/");
    return normalizeMediaLibraryPath(decoded);
}

export function mediaLibraryPageBasePath(pathname: string, workspaceId: string): string {
    const pathOnly = pathname.split("?")[0]?.split("#")[0] ?? pathname;
    const prefix = `/w/${workspaceId}/`;
    if (pathOnly.startsWith(prefix) !== true) {
        return pathOnly;
    }
    const afterWorkspace = pathOnly.slice(prefix.length);
    const pageKeySeparator = afterWorkspace.indexOf("/");
    const pageKey = pageKeySeparator === -1 ? afterWorkspace : afterWorkspace.slice(0, pageKeySeparator);
    return `${prefix}${pageKey}`;
}

export function buildMediaLibraryBrowserHref({
    libraryPath,
    pathname,
    search,
    workspaceId,
}: {
    libraryPath: string;
    pathname: string;
    search: string;
    workspaceId: string;
}): string {
    const basePath = mediaLibraryPageBasePath(pathname, workspaceId);
    const encoded = encodeMediaLibraryPathForUrl(libraryPath);
    const nextPath = encoded === "" ? basePath : `${basePath}/${encoded}`;
    return `${nextPath}${search}`;
}

export function isMediaLibraryDirentIdSplat(splat: string): boolean {
    const trimmed = splat.trim();
    if (trimmed.includes("/") === true) {
        return false;
    }
    return uuidZodValidator.safeParse(trimmed).success === true;
}

/**
 * Public file URL by id:
 * `{MEDIA_LIBRARY_PUBLIC_BASE_URL}/sub-system/pluto/w/{workspaceId}/media/id/{direntId}`
 */
export function buildMediaLibraryNeptuneIdUrl({
    cacheBust,
    direntId,
    intellsysBaseUrl: _intellsysBaseUrl,
    workspaceId,
}: {
    cacheBust?: string;
    direntId: Uuid;
    /** Unused for public host. Kept for call-site compatibility; host comes from MEDIA_LIBRARY_PUBLIC_BASE_URL. */
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): string {
    const url = `${plutoPublicMediaBase()}/w/${workspaceId}/media/id/${direntId}`;
    return appendMediaLibraryCacheBust({cacheBust: cacheBust, url: url});
}

/**
 * Public file URL by library path:
 * `{MEDIA_LIBRARY_PUBLIC_BASE_URL}/sub-system/pluto/w/{workspaceId}/media/path/{libraryPath}`
 */
export function buildMediaLibraryNeptunePathUrl({
    cacheBust,
    intellsysBaseUrl: _intellsysBaseUrl,
    path,
    workspaceId,
}: {
    cacheBust?: string;
    /** Unused for public host. Kept for call-site compatibility; host comes from MEDIA_LIBRARY_PUBLIC_BASE_URL. */
    intellsysBaseUrl: string;
    path: string;
    workspaceId: Uuid;
}): string {
    const url =
        `${plutoPublicMediaBase()}/w/${workspaceId}/media/path/${encodeMediaLibraryPathForUrl(path)}`;
    return appendMediaLibraryCacheBust({cacheBust: cacheBust, url: url});
}

function appendMediaLibraryCacheBust({
    cacheBust,
    url,
}: {
    cacheBust?: string;
    url: string;
}): string {
    if (cacheBust === undefined || cacheBust.trim() === "") {
        return url;
    }
    const parsed = new URL(url);
    parsed.searchParams.set("v", cacheBust);
    return parsed.toString();
}
